import { useMemo } from "react";
import { useAuth } from "./useAuth";
import {
  resolveDisplayCurrency,
  formatCurrency,
  formatCurrencyShort,
  formatCurrencyDash,
  getCurrencySymbol,
  currencyLabel,
  type CurrencyCode,
} from "@/lib/currency";

interface DashboardCurrency {
  /** Resolved currency code (e.g. 'USD', 'PLN', 'EUR') */
  currency: CurrencyCode;
  /** Format value with 2 decimal places: "$1,234.56" */
  fmt: (value: number) => string;
  /** Format value with 0 decimal places: "$1,235" */
  fmtShort: (value: number) => string;
  /** Format value or "—" if zero */
  fmtDash: (value: number) => string;
  /** Currency symbol: "$", "zł", "€" */
  symbol: string;
  /** Currency label for UI: "(USD)", "(PLN)" */
  label: string;
}

/**
 * Resolves the active currency for dashboard display.
 *
 * Priority: user preference → ad account currency → PLN fallback.
 *
 * @param accountCurrency - Currency from the active ad account (Meta/Google)
 */
export function useDashboardCurrency(accountCurrency?: string | null): DashboardCurrency {
  const { user } = useAuth();
  const pref = (user as any)?.preferredCurrency ?? null;

  return useMemo(() => {
    const currency = resolveDisplayCurrency(pref, accountCurrency);
    return {
      currency,
      fmt: (v: number) => formatCurrency(v, currency),
      fmtShort: (v: number) => formatCurrencyShort(v, currency),
      fmtDash: (v: number) => formatCurrencyDash(v, currency),
      symbol: getCurrencySymbol(currency),
      label: currencyLabel(currency),
    };
  }, [pref, accountCurrency]);
}
